import { ElectricalServices } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { DecarbContentLayout } from '../components/DecarbContentLayout';
import { DecarbOverviewHeader } from '../components/DecarbOverviewHeader';
import { FilterMenu, SearchBox, SortMenu } from '../components/TechToolbar';
import {
  EnergyKind,
  PlugLoadWithMetrics,
  TechSortDimensions,
  TechSortState,
  getEnergyField,
  sortTechItemsByState,
} from '../types';
import { PlugLoadUnit } from './PlugLoadUnit';

type PlugLoadSelectionDetailsProps = {
  helperTextComponent: React.ReactNode;
  availableItems: PlugLoadWithMetrics[];
  selectedItems: (PlugLoadWithMetrics & { count: number })[];
  costEnergyAndCoverageMetrics?: {
    // undefined: no stored figure to show yet (renders the placeholder)
    cost?: number;
    energyDemand: number;
    systemCoveragePercentage: number;
  };
  isLoading?: boolean;
  onAdd: (item: PlugLoadWithMetrics) => void;
  onRemove: (item: PlugLoadWithMetrics & { count: number }) => void;
  onChangeCount: (
    item: PlugLoadWithMetrics & { count: number },
    newCount: number,
  ) => void;
  readOnly?: boolean;
};

export const PlugLoadSelectionDetails: React.FC<
  PlugLoadSelectionDetailsProps
> = ({
  helperTextComponent,
  availableItems,
  selectedItems,
  costEnergyAndCoverageMetrics,
  isLoading,
  onAdd,
  onRemove,
  onChangeCount,
  readOnly,
}) => {
  const { cost, energyDemand } = costEnergyAndCoverageMetrics || {};

  const [searchString, setSearchString] = useState('');
  const [sortState, setSortState] = useState<TechSortState>({
    dimension: TechSortDimensions[0],
    direction: 'asc',
  });
  const [filter, setFilter] = useState<EnergyKind[]>([]);

  const availableEnergyTypes = useMemo(() => {
    const types = new Set(
      availableItems.map((item) => getEnergyField(item)).filter(Boolean),
    );
    return [...types] as EnergyKind[];
  }, [availableItems]);

  const filteredAndSortedItems = useMemo(() => {
    let items = availableItems;

    if (searchString.length > 0) {
      items = items.filter((item) =>
        item.name.toLowerCase().includes(searchString.toLowerCase()),
      );
    }

    if (filter.length > 0) {
      items = items.filter((item) => {
        const energy = getEnergyField(item);
        return energy ? filter.includes(energy as EnergyKind) : true;
      });
    }

    items = sortTechItemsByState(items, sortState);

    return items;
  }, [availableItems, searchString, filter, sortState]);

  return (
    <DecarbContentLayout
      availableItems={
        readOnly
          ? []
          : filteredAndSortedItems.map((item) => (
              <PlugLoadUnit
                key={item.id}
                item={item}
                selected={false}
                actions={{ onAdd: () => onAdd(item) }}
              />
            ))
      }
      selectedItems={selectedItems.map((item) => (
        <PlugLoadUnit
          key={item.id}
          count={item.count}
          item={item}
          selected={true}
          isLoading={isLoading}
          readOnly={readOnly}
          actions={{
            onRemove: () => onRemove(item),
            onIncrement: () => onChangeCount(item, item.count + 1),
            onDecrement: () => onChangeCount(item, item.count - 1),
            onChangeCount: (newCount: number) => onChangeCount(item, newCount),
          }}
        />
      ))}
      overviewHeader={
        <DecarbOverviewHeader
          systemCoverageName={'Plug Load'}
          systemCoverageIcon={<ElectricalServices />}
          systemCoverageHelperText={
            'Choose enough plug loads to get 100% coverage'
          }
          energyDemand={energyDemand}
          capitalCost={cost}
          coveragePercentage={
            costEnergyAndCoverageMetrics?.systemCoveragePercentage
          }
        />
      }
      helperText={helperTextComponent}
      searchComponent={
        readOnly ? undefined : (
          <SearchBox query={searchString} setQuery={setSearchString} />
        )
      }
      filterComponent={
        readOnly ? undefined : availableEnergyTypes.length > 1 ? (
          <FilterMenu
            listOfAllOptions={availableEnergyTypes}
            selectedOptions={filter}
            onReset={() => setFilter([])}
            onSelect={(option: EnergyKind) =>
              setFilter((prev) =>
                prev.includes(option)
                  ? prev.filter((val) => val !== option)
                  : [...prev, option],
              )
            }
          />
        ) : undefined
      }
      sortComponent={
        readOnly ? undefined : (
          <SortMenu
            dimensions={TechSortDimensions}
            activeSort={sortState}
            onSortChange={setSortState}
          />
        )
      }
    />
  );
};
